import type { Transaction, TransactionSpec } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import {
  isValidElement,
  useCallback,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useIntl } from 'react-intl';

import { useErrorLogsStore } from './useErrorLogsStore.js';
import { useReferencedState } from './useReferencedState.js';
import { usePrevious } from '../../../core/hooks/usePrevious.js';
import { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import { useFormFieldValidation } from '../../../forms/forms-core/hooks/useFormFieldValidation.js';
import type { FilterFieldAutoSuggestionsContextProps } from '../contexts/FilterFieldContext.types.js';
import {
  ANNOTATION_SUGGESTION_APPLY,
  ariaAttributesCompartment,
  stringAnnotation,
} from '../editor-state-configs/editor-config.js';
import { filterFieldMessages } from '../messages.js';
import type {
  FilterFieldSuggestion,
  FilterFieldSuggestionProps,
} from '../suggestions/Suggestion.types.js';
import type {
  FilterFieldSuggestionsContext,
  InternalFilterFieldSuggestionsCallback,
} from '../suggestions/suggestions-extension.js';
import type {
  FilterFieldGroupedSuggestions,
  FilterFieldSuggestionsCallback,
} from '../types/suggestions.js';
import type { FilterFieldNodeBase } from '../types/tree-nodes.js';
import {
  getCurrentFocusableListItem,
  type VirtualFocusItem,
} from '../utils/get-next-suggestion.js';
import { getSuggestionTransaction } from '../utils/get-suggestion-transaction.js';
import type { FilterFieldSuggestionRecentPinnedSuggestion } from '../utils/suggestions-utils/recent-and-pinned-suggestions.js';

/**
 * Suggestion arrays can contain ReactNode values (e.g. icons on custom types).
 * In dev mode, React elements created via jsxDEV() (used by @vitejs/plugin-react-swc
 * with development:true) carry an _owner property pointing to the currently rendering
 * Fiber, which has circular back-references. JSON.stringify would throw on those.
 * The replacer intercepts React elements before _owner is traversed, replacing each
 * element with a stable string token derived from its type.
 * @internal
 */
export function stringifySuggestions(value: unknown): string {
  return JSON.stringify(value, replacer);
}

function replacer(_key: string, value: unknown): unknown {
  if (isValidElement(value)) {
    const type = value.type;
    return `$$ReactElement:${
      typeof type === 'string'
        ? type
        : (type as { displayName?: string; name?: string }).displayName ??
          (type as { displayName?: string; name?: string }).name ??
          'Component'
    }`;
  }

  return value;
}

export function useSuggestions(props: {
  ariaDisabled: boolean | undefined;
  autoSuggestions: boolean;
  isOpen: boolean;
  id: string;
  suggestionsOverlayRef: MutableRefObject<HTMLDivElement | null>;
  editorViewRef: MutableRefObject<EditorView | null>;
  viewDispatch: (
    args: Transaction | TransactionSpec | TransactionSpec[],
  ) => void;
  onSuggest?: FilterFieldSuggestionsCallback;
  hiddenInputRef: MutableRefObject<HTMLInputElement | null>;
  ariaLabelingProps?: AriaLabelingProps;
}) {
  const {
    ariaDisabled,
    autoSuggestions,
    isOpen,
    id,
    suggestionsOverlayRef,
    editorViewRef,
    onSuggest,
    viewDispatch,
    hiddenInputRef,
    ariaLabelingProps,
  } = props;

  const setUserAction = useErrorLogsStore((state) => state.setAction);
  const { ariaProps } = useFormFieldValidation(
    hiddenInputRef,
    {},
    ariaLabelingProps,
  );

  const [
    autoSuggestionValues,
    setAutoSuggestionValues,
    autoSuggestionValuesRef,
  ] = useReferencedState<FilterFieldGroupedSuggestions>({});

  const [
    recentSuggestionValues,
    setRecentSuggestionValues,
    recentSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestionRecentPinnedSuggestion[]>([]);

  const [
    pinnedSuggestionValues,
    setPinnedSuggestionValues,
    pinnedSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestionRecentPinnedSuggestion[]>([]);

  const [
    logicalOperatorSuggestionValues,
    setLogicalOperatorSuggestionValues,
    logicalOperatorSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestion[]>([]);

  const [
    pastedContentSuggestionValues,
    setPastedContentSuggestionValues,
    pastedContentSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestion[]>([]);

  const [
    searchSuggestionValues,
    setSearchSuggestionValues,
    searchSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestion[]>([]);

  const [
    fallbackKeySuggestionValues,
    setFallbackKeySuggestionValues,
    fallbackKeySuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestion[]>([]);

  const [
    statementSuggestionValues,
    setStatementSuggestionValues,
    statementSuggestionValuesRef,
  ] = useReferencedState<FilterFieldSuggestion[]>([]);

  const [prevAutoSuggestionValues, setPrevAutoSuggestionValues] =
    useState<FilterFieldGroupedSuggestions>({});

  const [prevRecentSuggestionValues, setPrevRecentSuggestionValues] = useState<
    FilterFieldSuggestionRecentPinnedSuggestion[]
  >([]);

  const [
    prevPastedContentSuggestionValues,
    setPrevPastedContentSuggestionValues,
  ] = useState<FilterFieldSuggestion[]>([]);

  const [prevSearchSuggestionValues, setPrevSearchSuggestionValues] = useState<
    FilterFieldSuggestion[]
  >([]);

  const [prevFallbackKeySuggestionValues, setPrevFallbackKeySuggestionValues] =
    useState<FilterFieldSuggestion[]>([]);

  const [prevStatementSuggestionValues, setPrevStatementSuggestionValues] =
    useState<FilterFieldSuggestion[]>([]);

  const [focusedListItem, setFocusedListItem] = useState<
    VirtualFocusItem | undefined
  >();
  // Previously focused suggestion. Used to trigger an update of the attributes
  // compartment when rendering to avoid an additional re-render.
  const prevFocusedListItem = usePrevious(focusedListItem);

  // The currently entered last term to use for highlighting matching parts of suggestions.
  const suggestionsContext = useRef<FilterFieldSuggestionsContext | undefined>(
    undefined,
  );

  // Suggestions callback to determine which suggestions to show.
  const _onSuggest: InternalFilterFieldSuggestionsCallback = useCallback(
    (args) => {
      suggestionsContext.current = args.suggestionsContext;

      if (
        autoSuggestions &&
        stringifySuggestions(args.autoSuggestionValues) !==
          stringifySuggestions(autoSuggestionValuesRef.current)
      ) {
        setAutoSuggestionValues(args.autoSuggestionValues);
      }

      if (
        stringifySuggestions(args.recentSuggestionValues) !==
        stringifySuggestions(recentSuggestionValuesRef.current)
      ) {
        setRecentSuggestionValues(args.recentSuggestionValues);
      }

      if (
        stringifySuggestions(args.pinnedSuggestionValues) !==
        stringifySuggestions(pinnedSuggestionValuesRef.current)
      ) {
        setPinnedSuggestionValues(args.pinnedSuggestionValues);
      }

      if (
        stringifySuggestions(args.logicalOperatorSuggestionValues) !==
        stringifySuggestions(logicalOperatorSuggestionValuesRef.current)
      ) {
        setLogicalOperatorSuggestionValues(
          args.logicalOperatorSuggestionValues,
        );
      }

      if (
        stringifySuggestions(args.pastedContentSuggestionValues) !==
        stringifySuggestions(pastedContentSuggestionValuesRef.current)
      ) {
        setPastedContentSuggestionValues(args.pastedContentSuggestionValues);
      }

      if (
        stringifySuggestions(args.searchSuggestionValues) !==
        stringifySuggestions(searchSuggestionValuesRef.current)
      ) {
        setSearchSuggestionValues(args.searchSuggestionValues);
      }

      if (
        stringifySuggestions(args.fallbackKeySuggestionValues) !==
        stringifySuggestions(fallbackKeySuggestionValuesRef.current)
      ) {
        setFallbackKeySuggestionValues(args.fallbackKeySuggestionValues);
      }

      if (
        stringifySuggestions(args.filterStatementSuggestionValues) !==
        stringifySuggestions(statementSuggestionValuesRef.current)
      ) {
        setStatementSuggestionValues(args.filterStatementSuggestionValues);
      }
    },
    [
      autoSuggestions,
      autoSuggestionValuesRef,
      recentSuggestionValuesRef,
      pinnedSuggestionValuesRef,
      logicalOperatorSuggestionValuesRef,
      pastedContentSuggestionValuesRef,
      searchSuggestionValuesRef,
      fallbackKeySuggestionValuesRef,
      statementSuggestionValuesRef,
      setAutoSuggestionValues,
      setRecentSuggestionValues,
      setPinnedSuggestionValues,
      setLogicalOperatorSuggestionValues,
      setPastedContentSuggestionValues,
      setSearchSuggestionValues,
      setFallbackKeySuggestionValues,
      setStatementSuggestionValues,
    ],
  );

  const [prevOpen, setPrevOpen] = useState(isOpen);

  const intl = useIntl();
  const contentEditableDivAriaLabel = intl.formatMessage(
    filterFieldMessages.ariaLabel,
  );

  const suggestionsOverlayHintId = 'suggestions-overlay-announcement';
  const ariaDescribedBys = ariaProps['aria-describedby'] ?? '';
  const suggestionsHintId = isOpen ? suggestionsOverlayHintId : '';
  const ariaLabelProp = ariaLabelingProps?.['aria-label'];
  const ariaDetailsProp = ariaLabelingProps?.['aria-details'] ?? '';
  const ariaLabelledbyProp = ariaLabelingProps?.['aria-labelledby'] ?? '';

  const ariaAttributes = useMemo(
    () => ({
      'aria-label': ariaLabelProp ?? contentEditableDivAriaLabel,
      'aria-autocomplete': 'list',
      'aria-haspopup': 'listbox',
      ...(isOpen && {
        'aria-controls': `suggestions-${id}`,
      }),
      ...(focusedListItem?.id && {
        'aria-activedescendant': focusedListItem?.id,
      }),
      ...(ariaDisabled && {
        'aria-disabled': `${ariaDisabled}`,
      }),
      'aria-describedby': (ariaDescribedBys + ' ' + suggestionsHintId).trim(),
      ...(ariaDetailsProp && {
        'aria-details': `${ariaDetailsProp}`,
      }),
      ...(ariaLabelledbyProp && {
        'aria-labelledby': `${ariaLabelledbyProp}`,
      }),
    }),
    [
      ariaDescribedBys,
      ariaDisabled,
      ariaLabelProp,
      ariaDetailsProp,
      ariaLabelledbyProp,
      contentEditableDivAriaLabel,
      focusedListItem,
      isOpen,
      suggestionsHintId,
      id,
    ],
  );

  /**
   * Apply the given suggested value to the document, replacing the selection
   * so that the cursor ends up at the end of the inserted text.
   */
  const applySuggestion = useCallback(
    (
      element: HTMLElement | null,
      value: string,
      trailing: string | undefined,
      insertionStrategy: FilterFieldSuggestionProps['insertionStrategy'],
      cursorBeforeTrailing: boolean | undefined,
      range: FilterFieldNodeBase['range'] | undefined,
      shouldEscapeSuggestion?: boolean,
    ) => {
      if (!editorViewRef.current || !suggestionsContext.current || !element) {
        return;
      }

      setUserAction({
        type: 'applySuggestion',
        data: {
          value,
          trailing,
          insertionStrategy,
          cursorBeforeTrailing,
        },
      });

      const suggestionTransaction = getSuggestionTransaction(
        value,
        range,
        shouldEscapeSuggestion,
        trailing,
        insertionStrategy,
        cursorBeforeTrailing,
        suggestionsContext.current,
        editorViewRef.current.state,
      );

      // The applied suggestion will always override the last token.
      viewDispatch({
        ...suggestionTransaction,
        annotations: stringAnnotation.of(ANNOTATION_SUGGESTION_APPLY),
      });
      setFocusedListItem(undefined);
    },
    // Excluding suggestionsContext. We are accessing the current context by reference, will always be up to date.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFocusedListItem, viewDispatch],
  );

  if (prevOpen && !isOpen) {
    // Defer triggering the onSuggest callback, else the filter field is still rendering
    // while the callback may already update the suggestions, which would lead to a runtime error.
    // (Cannot update a component while rendering a different component)
    queueMicrotask(() => {
      onSuggest?.();
    });
  }

  // Update the currently focused suggestion.
  if (
    prevOpen !== isOpen ||
    autoSuggestionValuesRef.current !== prevAutoSuggestionValues ||
    recentSuggestionValuesRef.current !== prevRecentSuggestionValues ||
    pastedContentSuggestionValuesRef.current !==
      prevPastedContentSuggestionValues ||
    searchSuggestionValuesRef.current !== prevSearchSuggestionValues ||
    fallbackKeySuggestionValuesRef.current !==
      prevFallbackKeySuggestionValues ||
    statementSuggestionValuesRef.current !== prevStatementSuggestionValues
  ) {
    if (isOpen) {
      // If the currently focused suggestion is still in the document, do nothing.
      if (
        !getCurrentFocusableListItem(
          suggestionsOverlayRef.current,
          focusedListItem?.id,
        )
      ) {
        setFocusedListItem(undefined);
      }
    }
    setPrevAutoSuggestionValues(autoSuggestionValuesRef.current ?? {});
    setPrevRecentSuggestionValues(recentSuggestionValuesRef.current ?? []);
    setPrevPastedContentSuggestionValues(
      pastedContentSuggestionValuesRef.current ?? [],
    );
    setPrevSearchSuggestionValues(searchSuggestionValuesRef.current ?? []);
    setPrevFallbackKeySuggestionValues(
      fallbackKeySuggestionValuesRef.current ?? [],
    );
    setPrevStatementSuggestionValues(
      statementSuggestionValuesRef.current ?? [],
    );
    setPrevOpen(isOpen);
  }

  // Update aria attributes if overlay state or focused suggestion changes.
  if (prevFocusedListItem !== focusedListItem || prevOpen !== isOpen) {
    viewDispatch({
      effects: ariaAttributesCompartment.reconfigure(
        EditorView.contentAttributes.of(ariaAttributes),
      ),
    });
  }

  const handleNextSuggestion = (nextSuggestion: HTMLElement | null) => {
    if (nextSuggestion) {
      const id = nextSuggestion.dataset.stratoFilterFieldVirtualFocusId;
      const type = nextSuggestion.dataset
        .stratoFilterFieldVirtualFocusType as VirtualFocusItem['type'];
      setFocusedListItem({ id, type });
      nextSuggestion.scrollIntoView({ block: 'center' });
    }
  };

  const autoSuggestionsConfig =
    useMemo<FilterFieldAutoSuggestionsContextProps>((): FilterFieldAutoSuggestionsContextProps => {
      const filterValue = suggestionsContext.current?.currentStatement?.value;
      const cursorPosition = editorViewRef.current?.state.selection.main.head;

      return {
        trailing: cursorPosition === filterValue?.range.to ? ' ' : undefined,
        groupedSuggestions: autoSuggestionValues,
      };
      // Excluding editorViewRef. We are accessing the current value by reference, will always be up to date.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSuggestionValues]);

  return {
    ariaAttributes,
    autoSuggestionsConfig,
    recentSuggestionValues,
    pinnedSuggestionValues,
    logicalOperatorSuggestionValues,
    pastedContentSuggestionValues,
    searchSuggestionValues,
    fallbackKeySuggestionValues,
    statementSuggestionValues,
    focusedListItem,
    suggestionsContext,
    applySuggestion,
    handleNextSuggestion,
    _onSuggest,
    suggestionsOverlayHintId: suggestionsOverlayHintId,
  };
}
